import {
  isByosMediaCloudFrontUrl,
  isByosOriginalCloudFrontUrl,
  isByosOriginalImgixUrl,
  needsImgixServerSign,
  rewriteByosOriginalCloudFrontToImgix,
} from '@air/component-imgix-image';

/**
 * Maps an Air-managed Imgix URL onto the Air media CloudFront host for Imgix-outage fallbacks.
 *
 * Returns '' when there is no public CloudFront equivalent (BYOS proxy Imgix, leftover per-pool
 * original Imgix, handshake-locked original CloudFront, or BYOS media CloudFront). Callers must
 * treat '' as "no fallback": do not put it on `<img src>`, and do not skip rendering the primary
 * image because this helper is empty.
 */
export const getCloudFrontUrlFromImgixUrl = (url: string) => {
  if (!url) {
    return '';
  }

  if (
    isByosOriginalImgixUrl(url) ||
    isByosOriginalCloudFrontUrl(url) ||
    isByosMediaCloudFrontUrl(url) ||
    rewriteByosOriginalCloudFrontToImgix(url)
  ) {
    return '';
  }

  const baseImgixUrl = `.imgix.net/`;
  const baseS3Url = process.env.NEXT_PUBLIC_AIR_MEDIA_BUCKET_URL;
  const file = url.split(baseImgixUrl)[1];

  return `${baseS3Url}/${file}`;
};

const urlWithoutSearch = (url: string): string => {
  try {
    const parsed = new URL(url);
    parsed.search = '';

    return parsed.toString();
  } catch {
    return url;
  }
};

/**
 * Plain `<img>` URL when an Imgix rendition fails.
 * Air-managed Imgix rewrites to CloudFront. A JPEG already on BYOS media CloudFront
 * has no Air bucket equivalent, so the stored URL is the fallback. Handshake-locked
 * originals and BYOS proxy URLs stay empty — those have to be requested through Imgix.
 */
export const getImagePreviewFallbackUrl = (url: string): string => {
  if (!url) {
    return '';
  }

  const withoutSearch = urlWithoutSearch(url);
  const cloudFrontUrl = getCloudFrontUrlFromImgixUrl(withoutSearch);

  if (cloudFrontUrl !== '') {
    return cloudFrontUrl;
  }

  if (isByosOriginalCloudFrontUrl(withoutSearch) || needsImgixServerSign(withoutSearch)) {
    return '';
  }

  return withoutSearch;
};
